import { deepGet, defineSmartGetter } from '../utils';
import type { DeepPartial } from '../types';

export interface ThemeLeaf<Value> {
  $type: string;
  $value: Value;
  $constant?: boolean;
  $comment?: string;
}

export type ThemeDefinition = {
  [key: string]: ThemeLeaf<unknown> | ThemeDefinition;
};

export type ToThemeIFace<Node, Extension = never> =
  Node extends ThemeLeaf<infer LeafVal>
    ? (LeafVal extends null ? LeafVal | string : LeafVal) | Extension
    : Node extends object
      ? {
          readonly [Key in keyof Node as Key extends '$comment' ? never : Key]: ToThemeIFace<
            Node[Key],
            Extension
          >;
        }
      : Node | Extension;

export type SettableTheme<Def extends ThemeDefinition> = DeepPartial<ToThemeIFace<Def, symbol>>;

export const resetToInitial = Symbol.for('@pega/cosmos-react-core.theme.resetToInitial');

export interface ThemeMachineLike<Definition extends ThemeDefinition> {
  parent: ThemeMachineLike<Definition> | null;

  definition: Definition;

  overrides: Map<string, boolean>;

  readonly theme: ToThemeIFace<Definition>;
}

class ThemeMachine<Definition extends ThemeDefinition> implements ThemeMachineLike<Definition> {
  parent: ThemeMachineLike<Definition> | null;

  definition: Definition;

  overrides: Map<string, boolean>;

  readonly theme: ToThemeIFace<Definition>;

  overrideInTree(keys: string[]): boolean {
    let ancestor: ThemeMachineLike<Definition> | null = this;
    const fullProp = keys.join('.');

    while (ancestor) {
      // Trigger getters in the ancestor themes.
      deepGet(ancestor.theme, keys);

      if (ancestor.overrides.has(fullProp)) return !!ancestor.overrides.get(fullProp);
      ancestor = ancestor.parent;
    }

    return false;
  }

  constructTheme(theme: SettableTheme<Definition>): ToThemeIFace<Definition> {
    const constructForNode = (keys: string[] = []) => {
      const themeNode: Record<string, any> = {};

      Object.keys(deepGet(this.definition, keys)).forEach(key => {
        // Ignore comment nodes.
        if (key === '$comment') return;

        const propKeys = [...keys, key];
        const defNode: Record<string, any> = deepGet(this.definition, propKeys);

        // If it is not a leaf node of the theme definition...
        if (defNode.$type === undefined) {
          // recursively run for child nodes.
          defineSmartGetter(themeNode, key, () => constructForNode(propKeys));
          return;
        }

        const themeValue = deepGet(theme, propKeys);

        // If a theme value was set for this ThemeMachine...
        if (themeValue !== undefined) {
          // set overrides to true, unless it is being reset.
          this.overrides.set(propKeys.join('.'), themeValue !== resetToInitial);
        }

        // If a theme value was set for this ThemeMachine, is not being reset, and is for a non-constant property...
        if (themeValue !== undefined && themeValue !== resetToInitial && !defNode.$constant) {
          // set to the provided value.
          themeNode[key] = themeValue;
        }

        // If the property has been overridden in the tree (and not reset)...
        else if (this.overrideInTree(propKeys)) {
          // set to the value in the parent theme.
          defineSmartGetter(themeNode, key, () => deepGet(this.parent!.theme, propKeys));
        }

        // If the property type is inherited...
        else if (defNode.$type === 'inherited') {
          // set to the value of the inherited property in this theme.
          defineSmartGetter(themeNode, key, () => deepGet(this.theme, defNode.$value.split('.')));
        }

        // If the property type is literal...
        else if (defNode.$type === 'literal') {
          // set to the value listed in the definition.
          themeNode[key] = defNode.$value;
        }
      });

      return themeNode;
    };

    return constructForNode() as ToThemeIFace<Definition>;
  }

  constructor({
    theme = {} as SettableTheme<Definition>,
    parent,
    definition
  }:
    | {
        theme?: SettableTheme<Definition>;
        parent: ThemeMachineLike<Definition>;
        definition?: never;
      }
    | { theme?: SettableTheme<Definition>; parent?: never; definition: Definition }) {
    this.parent = parent ?? null;
    this.definition = parent ? parent.definition : definition;
    this.overrides = new Map();
    this.theme = this.constructTheme(theme);
  }
}

export default ThemeMachine;
